import React, { useState } from "react"
import classNames from "classnames"
import { Range } from "react-range"
import {
  getSoundEffectsVolume,
  getTimerVolume,
  setSoundEffectsVolume,
  setTimerVolume,
} from "@/utils/volumePreference"
import { useBackgroundMusicPreference } from "@/Hooks/useBackgroundMusicPreference"
import { playSliderTickSound, previewTimerTickSound } from "@/utils/audioManager"
import {
  AUTO_MUSIC_TRACK,
  MusicTrackSelection,
  getMusicTrackSelection,
  musicTracks,
  setMusicTrackSelection,
} from "@/utils/musicTrackPreference"

interface VolumeSliderProps {
  label: string
  hint: string
  value: number
  onChange: (value: number) => void
  /** Fired when the drag ends — used to play a sample at the new level. */
  onCommit?: (value: number) => void
  testIdPrefix: string
}

const volumeIcon = (value: number) => {
  if (value <= 0) return "fa-volume-xmark"
  if (value < 0.35) return "fa-volume-off"
  if (value < 0.7) return "fa-volume-low"
  return "fa-volume-high"
}

const VolumeSlider: React.FC<VolumeSliderProps> = ({
  label,
  hint,
  value,
  onChange,
  onCommit,
  testIdPrefix,
}) => {
  const percent = Math.round(value * 100)

  // Below 1000px index.css halves the root font size, so every rem step here
  // lands at half of what it reads as on desktop — `text-sm` is 7px and the
  // drag handle a 10px dot. The `mobile:` px steps restate the sizes that have
  // to survive that, the handle first: it is the one thing in this panel you
  // have to hit with a thumb.
  return (
    <div className="rounded-2xl border border-white/10 bg-black/30 p-4 mobile:rounded-[12px] mobile:p-[10px]">
      <div className="mb-1 flex items-center gap-3 mobile:gap-[8px]">
        <i
          className={classNames(
            "fa-solid w-5 text-center text-lg mobile:w-[16px] mobile:text-[14px]",
            volumeIcon(value),
            value > 0 ? "text-amber-300" : "text-white/30"
          )}
        ></i>
        <span className="flex-1 font-poppins text-sm font-semibold text-white mobile:text-[13px]">
          {label}
        </span>
        <span
          className={classNames(
            "w-12 text-right font-poppins text-sm font-bold tabular-nums mobile:w-[34px] mobile:text-[12px]",
            value > 0 ? "text-amber-200" : "text-white/40"
          )}
          data-testid={`${testIdPrefix}.value`}
        >
          {percent}%
        </span>
      </div>
      <p className="mb-4 pl-8 font-poppins text-xs text-white/50 mobile:mb-[10px] mobile:pl-[24px] mobile:text-[10px]">
        {hint}
      </p>

      <div className="px-1" data-testid={testIdPrefix}>
        <Range
          step={0.05}
          min={0}
          max={1}
          values={[value]}
          onChange={(values) => onChange(values[0])}
          onFinalChange={(values) => onCommit?.(values[0])}
          renderTrack={({ props, children }) => (
            <div
              {...props}
              className="relative h-2 w-full rounded-full bg-white/10 mobile:h-[6px]"
            >
              <div
                className="pointer-events-none absolute left-0 top-0 h-full rounded-full bg-gradient-to-r from-amber-400 to-amber-600"
                style={{ width: `${percent}%` }}
              />
              {children}
            </div>
          )}
          renderThumb={({ props, isDragged }) => (
            <div
              {...props}
              style={props.style}
              aria-label={label}
              className={classNames(
                "h-5 w-5 rounded-full border-2 border-amber-200 bg-amber-400 shadow-sliderThumb outline-none transition-transform mobile:h-[20px] mobile:w-[20px]",
                isDragged && "scale-110"
              )}
            />
          )}
        />
      </div>
    </div>
  )
}

interface MusicTrackPickerProps {
  value: MusicTrackSelection
  onChange: (selection: MusicTrackSelection) => void
  testIdPrefix: string
}

/**
 * Which tune loops behind the game. "Auto" plays them one after another and
 * starts over at the top; picking a track by name loops that one on its own.
 *
 * A pick takes effect on the spot — the tune swaps mid-game — because a picker
 * you have to leave and come back to can't be judged by ear.
 */
const MusicTrackPicker: React.FC<MusicTrackPickerProps> = ({
  value,
  onChange,
  testIdPrefix,
}) => {
  const renderOption = (
    id: MusicTrackSelection,
    name: string,
    icon: string,
    hint?: string
  ) => {
    const isSelected = id === value
    return (
      <button
        key={id}
        onClick={() => onChange(id)}
        data-testid={`${testIdPrefix}.option.${id}`}
        className={classNames(
          "flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-all mobile:gap-[8px] mobile:rounded-[10px] mobile:px-[9px] mobile:py-[8px]",
          isSelected
            ? "border-amber-400 bg-amber-500/20 ring-1 ring-amber-400/50"
            : "border-white/10 bg-black/20 hover:border-white/40 hover:bg-white/5"
        )}
      >
        <i
          className={classNames(
            "fa-solid w-4 text-center text-sm mobile:w-[14px] mobile:text-[12px]",
            icon,
            isSelected ? "text-amber-300" : "text-white/40"
          )}
        ></i>
        <span className="min-w-0 flex-1">
          <span
            className={classNames(
              "block truncate font-poppins text-sm font-semibold mobile:text-[12px]",
              isSelected ? "text-amber-100" : "text-white/80"
            )}
          >
            {name}
          </span>
          {hint && (
            <span className="block font-poppins text-[11px] text-white/50 mobile:truncate mobile:text-[10px]">
              {hint}
            </span>
          )}
        </span>
        {isSelected && (
          <i className="fa-solid fa-check text-xs text-amber-300 mobile:text-[11px]"></i>
        )}
      </button>
    )
  }

  return (
    <div
      className="rounded-2xl border border-white/10 bg-black/30 p-4 mobile:rounded-[12px] mobile:p-[10px]"
      data-testid={testIdPrefix}
    >
      <div className="mb-1 flex items-center gap-3 mobile:gap-[8px]">
        <i className="fa-solid fa-compact-disc w-5 text-center text-lg text-amber-300 mobile:w-[16px] mobile:text-[14px]"></i>
        <span className="flex-1 font-poppins text-sm font-semibold text-white mobile:text-[13px]">
          Music track
        </span>
      </div>
      <p className="mb-4 pl-8 font-poppins text-xs text-white/50 mobile:mb-[10px] mobile:pl-[24px] mobile:text-[10px]">
        Starts playing as soon as you pick it — and switches the music on if it
        was off.
      </p>

      {/* Auto is the mode rather than one more track, so it sits on its own row
          above the four. */}
      <div className="mb-2 flex flex-col mobile:mb-[8px]">
        {renderOption(
          AUTO_MUSIC_TRACK,
          "Auto",
          "fa-repeat",
          "Play every track in turn, then start over"
        )}
      </div>
      <div className="grid grid-cols-2 gap-2 mobile:gap-[8px]">
        {musicTracks.map((track) =>
          renderOption(track.id, track.name, "fa-music")
        )}
      </div>
    </div>
  )
}

/**
 * The music half of the sound settings: how loud the tune is and which tune it
 * is. Split out from SoundSettings because the navbar offers the music on its
 * own — sharing the component keeps the two places from drifting apart.
 */
export const MusicSettings: React.FC<{ testIdPrefix?: string }> = ({
  testIdPrefix = "settings",
}) => {
  const {
    volume: music,
    setVolume: setMusic,
    enabled: musicEnabled,
    toggle: toggleMusic,
  } = useBackgroundMusicPreference()
  const [track, setTrack] = useState(getMusicTrackSelection)

  // Stored first, so switching the music on below starts the tune that was just
  // picked rather than the one before it.
  const handleTrackChange = (selection: MusicTrackSelection) => {
    setTrack(selection)
    setMusicTrackSelection(selection)
    // Picking a tune is asking to hear it, so a muted game unmutes itself —
    // the same thing dragging the slider up does.
    if (!musicEnabled) toggleMusic()
  }

  return (
    <>
      <VolumeSlider
        label="Background music"
        hint="The tune that loops while you play. At 0 the music button in the game bar switches off too."
        value={music}
        onChange={setMusic}
        testIdPrefix={`${testIdPrefix}.music-volume`}
      />
      <MusicTrackPicker
        value={track}
        onChange={handleTrackChange}
        testIdPrefix={`${testIdPrefix}.music-track`}
      />
    </>
  )
}

/**
 * The three volume mixes: the looping background tune, every one-shot effect
 * (dice, turn bell, cards, money, ...), and the clock ticking that runs while
 * you are on a timer. All apply live — the tune and the tick are re-mixed while
 * they keep looping, and effects pick the new level up on their next play.
 *
 * The tick has its own slider because it is the only sound that plays
 * continuously: turning it down shouldn't cost you the dice and the money.
 *
 * The music slider is the same control as the note button in the game bar (see
 * useBackgroundMusicPreference): muting there drops this slider to 0, and
 * dragging it to 0 here switches that button off.
 */
const SoundSettings: React.FC<{ testIdPrefix?: string }> = ({
  testIdPrefix = "settings",
}) => {
  const [effects, setEffects] = useState(getSoundEffectsVolume)
  const [timer, setTimer] = useState(getTimerVolume)

  const handleEffectsChange = (value: number) => {
    setEffects(value)
    setSoundEffectsVolume(value)
  }

  const handleTimerChange = (value: number) => {
    setTimer(value)
    setTimerVolume(value)
  }

  return (
    <div className="flex max-w-2xl flex-col gap-4 mobile:gap-[10px]">
      <MusicSettings testIdPrefix={testIdPrefix} />
      <VolumeSlider
        label="Sound effects"
        hint="Dice, turn bell, cards, money and the rest of the game's sounds."
        value={effects}
        onChange={handleEffectsChange}
        // Played once the drag ends rather than on every step, so you hear the
        // level you landed on instead of a burst of ticks on the way there.
        onCommit={() => playSliderTickSound()}
        testIdPrefix={`${testIdPrefix}.effects-volume`}
      />
      <VolumeSlider
        label="Timer ticking"
        hint="The clock heard while you're on a timer — your turn, an auction, a card decision. Independent of the sound effects."
        value={timer}
        onChange={handleTimerChange}
        // A short sample of the tick itself, so the level can be judged without
        // waiting for a timer to come round.
        onCommit={() => previewTimerTickSound()}
        testIdPrefix={`${testIdPrefix}.timer-volume`}
      />
    </div>
  )
}

export default SoundSettings
